import {useMemo} from 'react';

import {type RunHistoryRow} from '../../../types/run';
import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {rowsWithKeysMemo} from '../common';

/**
 * Filters runs to only those that have media data for the specified keys.
 * Also builds a cache map of steps per run for performance.
 *
 * @param allRuns - All available runs (with or without media)
 * @param mediaKeys - Keys to check for media availability
 * @param skipFiltering - If true, return all runs without filtering (for compare/individual mode)
 * @returns Object containing filtered runs and a map of run name to steps
 */
export function useRunsWithMedia(
  allRuns: RunWithHistoryAndMediaWandb[],
  mediaKeys: string[] | undefined,
  skipFiltering = false
): {
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  runStepsMap: Map<string, RunHistoryRow[]>;
} {
  // Filter runs to only those with media for the specified keys
  // In compare/individual mode, skip filtering to keep all configured runs available
  const runsWithMedia = useMemo(() => {
    if (skipFiltering) {
      return allRuns;
    }

    const filtered = allRuns.filter(runHistory => {
      const steps = rowsWithKeysMemo(runHistory, mediaKeys);
      return steps.length > 0;
    });

    return filtered;
  }, [allRuns, mediaKeys, skipFiltering]);

  // Cache rowsWithKeysMemo results for each run to avoid recomputation
  const runStepsMap = useMemo(() => {
    const map = new Map<string, RunHistoryRow[]>();
    runsWithMedia.forEach(runHistory => {
      map.set(runHistory.name, rowsWithKeysMemo(runHistory, mediaKeys));
    });
    return map;
  }, [runsWithMedia, mediaKeys]);

  return {
    runsWithMedia,
    runStepsMap,
  };
}
